/* eslint-disable */
import { Component, Input, OnChanges, SimpleChanges, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { tap } from 'rxjs';
import { IEmployeeSCDItem } from 'src/app/shared/interfaces/IEmployeeSCDItem';
import { IScdPickerDialogData } from '../../interfaces/IScdPickerDialogData';
import { IEmployeePickerDialogData } from '../../interfaces/IEmployeePickerDialogData';
import { IProjectMember } from '../../interfaces/IProjectMember';
import { IEmployeeListItem } from '../../interfaces/IEmployeeListItem';
import { ScdPickerDialogComponent } from './scd-picker-dialog/scd-picker-dialog.component';
import { EmployeePickerDialogComponent } from './employee-picker-dialog/employee-picker-dialog.component';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import { IFilter } from '../../interfaces/IFilter';

@Component({
  selector: 'app-people-picker',
  templateUrl: './people-picker.component.html',
  styleUrls: ['./people-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatFormField, MatLabel, MatInput, MatSuffix, TranslatePipe],
})
export class PeoplePickerComponent implements OnChanges {
  private dialog = inject(MatDialog);

  @Input() multipleSelectionAllowed: boolean;
  @Input() label?: string;
  readonly disabled = input<boolean>();
  readonly isExternalUser = input<boolean>();
  readonly required = input<boolean>();
  readonly hideSelectedItems = input(false);
  readonly multipleSelectButtonText = input(false);
  readonly filter = input<IFilter[]>([]);
  readonly showError = input<boolean>(false);
  @Input() set items(val: IProjectMember[] | IEmployeeSCDItem | IEmployeeSCDItem[] | IEmployeeListItem | string) {
    this.storedItems = val;
    this.mappedItems = this.mapItems(val);
  }
  get items(): IProjectMember[] | IEmployeeSCDItem | IEmployeeSCDItem[] | string {
    return this.storedItems;
  }
  readonly selection = output<IEmployeeSCDItem[]>();

  public mappedItems: IEmployeeSCDItem[];
  private storedItems: IProjectMember[] | IEmployeeSCDItem | IEmployeeSCDItem[] | string;

  public shouldShowError(): boolean {
    return this.showError() && this.required() && !this.mappedItems?.length;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.items) {
      this.mappedItems = this.mapItems(this.items);
    }
  }

  public openPeopleSelectionDialog(multipleSelection: boolean): void {
    if (!this.isExternalUser()) {
      this.openEmployeePickerDialog(multipleSelection);
    } else {
      this.openScdPickerDialog(multipleSelection);
    }
  }

  public removeSelectedPerson(item?: IEmployeeSCDItem): void {
    if (!this.disabled()) {
      this.mappedItems = this.mappedItems.filter(mappedItem => mappedItem !== item);
      this.selection.emit(this.mappedItems);
    }
  }

  private mapItems(items: IProjectMember[] | IEmployeeSCDItem | IEmployeeSCDItem[] | string): IEmployeeSCDItem[] {
    if (this.isEmployeeSCDItemArray(items)) {
      return items;
    } else if (this.isProjectMemberArray(items)) {
      return items.map(item => item.employee);
    } else if (this.isString(items) && items !== '') {
      return [{ scdName: items, scdEMail: '', scdGid: '' }];
    } else if (this.isEmployeeSCDItem(items)) {
      return [items];
    } else {
      return null;
    }
  }

  private openEmployeePickerDialog(multipleSelection: boolean): void {
    const data: IEmployeePickerDialogData = {
      alreadySelectedEmployees: this.mappedItems,
      multipleSelection: multipleSelection,
      filter: this.filter(),
    };
    const employeePickerDialogRef = this.dialog.open(EmployeePickerDialogComponent, { data, panelClass: ['picker-dialog', 'difa-dialog'] });
    employeePickerDialogRef
      .afterClosed()
      .pipe(tap(result => this.checkPickerDialogResponse(result)))
      .subscribe();
  }

  private openScdPickerDialog(multipleSelection: boolean): void {
    const data: IScdPickerDialogData = {
      selectedEmployees: this.mappedItems,
      multipleSelection,
    };
    const employeePickerDialogRef = this.dialog.open(ScdPickerDialogComponent, { data, panelClass: ['picker-dialog', 'difa-dialog'] });
    employeePickerDialogRef
      .afterClosed()
      .pipe(tap(result => this.checkPickerDialogResponse(result)))
      .subscribe();
  }

  private checkPickerDialogResponse(response: IEmployeeSCDItem[] | undefined): void {
    if (this.isEmployeeSCDItemArray(response)) {
      this.updateSelectionAfterPicker(response);
    }
  }

  private updateSelectionAfterPicker(employees: IEmployeeSCDItem[]): void {
    this.mappedItems = employees;
    this.selection.emit(this.mappedItems);
  }

  private isProjectMemberArray(value: any): value is IProjectMember[] {
    return value?.length && this.isProjectMember(value[0]) && Array.isArray(value);
  }

  private isEmployeeSCDItemArray(value: any): value is IEmployeeSCDItem[] {
    return value?.length && this.isEmployeeSCDItem(value[0]) && Array.isArray(value);
  }

  private isProjectMember(value: any): value is IProjectMember {
    return (
      typeof value === 'object' &&
      value !== null &&
      'employee' in value &&
      typeof value.employee === 'object' &&
      'isActive' in value &&
      typeof value.isActive === 'boolean' &&
      'isRemovable' in value &&
      typeof value.isRemovable === 'boolean'
    );
  }

  private isEmployeeSCDItem(value: any): value is IEmployeeSCDItem {
    return (
      typeof value === 'object' &&
      value !== null &&
      'scdName' in value &&
      typeof value.scdName === 'string' &&
      'scdEMail' in value &&
      typeof value.scdEMail === 'string' &&
      'scdGid' in value &&
      typeof value.scdGid === 'string'
    );
  }

  private isString(value: any): value is string {
    return typeof value === 'string';
  }
}
